/**
 * useWiringReportData Hook
 *
 * Data fetching and aggregation hook for wiring reports.
 * Fetches cable paths, junctions, and scale data for all floor plans in a project.
 */

import { useMemo } from 'react';
import { useQuery, useQueries } from '@tanstack/react-query';
import { getAuthenticatedSupabaseClient } from '@/integrations/supabase/client';
import { getCablePaths, aggregateCableRequirements, calculatePathRealLength } from '@/services/cablePathService';
import { getCableJunctions } from '@/services/cableJunctionService';
import type { CablePath, CableJunction } from '@/types/cablePaths';
import type { ScaleData } from '@/types/floorPlanViewer';
import type {
  WiringReportData,
  WiringReportSummary,
  CableRequirementRow,
  CablePathDetailRow,
  CableDetail,
  JunctionBoxRow,
  ConduitScheduleRow,
  FloorPlanSummary,
  ReportFilters,
} from '@/types/reports';
import { GENERIC_CABLE_LABELS as CABLE_LABELS, PATH_TYPE_LABELS, PATH_ROUTE_LABELS } from '@/types/cablePaths';

interface UseWiringReportDataOptions {
  projectId: string;
  filters: ReportFilters;
}

interface FloorPlanWithScale {
  id: string;
  name: string;
  scale_data: ScaleData | null;
}

/**
 * Fetch all floor plans for a project with their scale data.
 */
async function fetchFloorPlansForProject(projectId: string): Promise<FloorPlanWithScale[]> {
  const supabase = await getAuthenticatedSupabaseClient();
  const { data, error } = await supabase
    .from('floor_plans')
    .select('id, name, scale_data')
    .eq('project_id', projectId)
    .order('name');

  if (error) throw error;
  return (data ?? []) as FloorPlanWithScale[];
}

/**
 * Get cable category from generic type.
 */
function getCableCategoryFromType(genericType: string | undefined): 'Data' | 'Lighting Control' | 'Power' | 'Low Voltage' | 'Other' {
  if (!genericType) return 'Other';

  const dataTypes = ['CAT5E', 'CAT6', 'CAT6A', 'CAT7', 'FIBER_SINGLE', 'FIBER_MULTI'];
  const controlTypes = ['DMX_3PIN', 'DMX_5PIN', 'CONTROL_DALI', 'CONTROL_0_10V', 'CONTROL_LUTRON', 'CONTROL_CRESTRON', 'CONTROL_LOW_VOLTAGE'];
  const powerTypes = ['POWER_12_2', 'POWER_12_3', 'POWER_14_2', 'POWER_14_3', 'POWER_10_2', 'POWER_10_3'];
  const lvTypes = ['LV_18_2', 'LV_16_2', 'LV_SPEAKER_14_2', 'LV_SPEAKER_14_4', 'LV_SPEAKER_16_2', 'LV_SPEAKER_16_4', 'COAX_RG6', 'COAX_RG59', 'HDMI', 'USB'];

  if (dataTypes.includes(genericType)) return 'Data';
  if (controlTypes.includes(genericType)) return 'Lighting Control';
  if (powerTypes.includes(genericType)) return 'Power';
  if (lvTypes.includes(genericType)) return 'Low Voltage';
  return 'Other';
}

/**
 * Build a junction coordinate map for path length calculations.
 */
function buildJunctionMap(junctions: CableJunction[]): Map<string, { x: number; y: number }> {
  const map = new Map<string, { x: number; y: number }>();
  for (const junction of junctions) {
    map.set(junction.id, { x: junction.x, y: junction.y });
  }
  return map;
}

/**
 * Calculate the length of a path using its junctions and scale data.
 */
function getPathLength(
  path: CablePath,
  junctionMap: Map<string, { x: number; y: number }>,
  scaleData: ScaleData | null
): number {
  const startJunction = path.start_junction_id ? junctionMap.get(path.start_junction_id) : null;
  const endJunction = path.end_junction_id ? junctionMap.get(path.end_junction_id) : null;
  return calculatePathRealLength(path.points, startJunction, endJunction, scaleData);
}

/**
 * Get the unit from scale data, defaulting to 'ft'.
 */
function getUnit(scaleData: ScaleData | null): string {
  return scaleData?.unit ?? 'ft';
}

export function useWiringReportData({ projectId, filters }: UseWiringReportDataOptions) {
  // Step 1: Fetch all floor plans for the project
  const {
    data: floorPlans,
    isLoading: floorPlansLoading,
    error: floorPlansError,
  } = useQuery({
    queryKey: ['project-floor-plans', projectId],
    queryFn: () => fetchFloorPlansForProject(projectId),
    enabled: !!projectId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  // Get filtered floor plan IDs
  const filteredFloorPlanIds = useMemo(() => {
    if (!floorPlans) return [];
    if (filters.floorPlanId === 'all') {
      return floorPlans.map(fp => fp.id);
    }
    return floorPlans.filter(fp => fp.id === filters.floorPlanId).map(fp => fp.id);
  }, [floorPlans, filters.floorPlanId]);

  // Step 2: Fetch cable paths for each floor plan (parallel queries)
  const pathQueries = useQueries({
    queries: filteredFloorPlanIds.map(fpId => ({
      queryKey: ['cable-paths', fpId],
      queryFn: () => getCablePaths(fpId),
      enabled: filteredFloorPlanIds.length > 0,
      staleTime: 60 * 1000, // 1 minute
    })),
  });

  // Step 3: Fetch cable junctions for each floor plan (parallel queries)
  const junctionQueries = useQueries({
    queries: filteredFloorPlanIds.map(fpId => ({
      queryKey: ['cable-junctions', fpId],
      queryFn: () => getCableJunctions(fpId),
      enabled: filteredFloorPlanIds.length > 0,
      staleTime: 60 * 1000, // 1 minute
    })),
  });

  // Step 4: Collect unique product IDs from all junctions to fetch product names
  const junctionProductIds = useMemo(() => {
    const productIds = new Set<string>();
    junctionQueries.forEach(q => {
      (q.data ?? []).forEach(junction => {
        if (junction.product_id) {
          productIds.add(junction.product_id);
        }
      });
    });
    return Array.from(productIds);
  }, [junctionQueries]);

  // Step 5: Fetch product names for junction products
  // Use staleTime to prevent refetching and enable as soon as we have product IDs
  const {
    data: junctionProducts,
    isLoading: productsLoading,
  } = useQuery({
    queryKey: ['report-junction-products', junctionProductIds],
    queryFn: async () => {
      if (junctionProductIds.length === 0) return [];
      const supabase = await getAuthenticatedSupabaseClient();
      const { data, error } = await supabase
        .from('product_library')
        .select('id, brand, model, part_number')
        .in('id', junctionProductIds);
      if (error) throw error;
      return data ?? [];
    },
    enabled: junctionProductIds.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes - products don't change often
  });

  // Build product name lookup map
  const productNameMap = useMemo(() => {
    const map = new Map<string, string>();
    (junctionProducts ?? []).forEach(p => {
      // Build display name: "Brand Model" or just model/brand
      const displayName = p.brand && p.model
        ? `${p.brand} ${p.model}`
        : p.model || p.brand || 'Unknown Product';
      map.set(p.id, displayName);
    });
    return map;
  }, [junctionProducts]);

  // Compute loading and error states
  // Don't block on products - show report as soon as paths/junctions are ready
  const pathsLoading = pathQueries.some(q => q.isLoading);
  const junctionsLoading = junctionQueries.some(q => q.isLoading);
  const coreDataLoading = floorPlansLoading || pathsLoading || junctionsLoading;
  const isLoading = coreDataLoading; // Products load in background

  const pathsError = pathQueries.find(q => q.error)?.error;
  const junctionsError = junctionQueries.find(q => q.error)?.error;
  const error = floorPlansError || pathsError || junctionsError;

  // Build the complete report data
  const reportData = useMemo<WiringReportData | null>(() => {
    if (!floorPlans || coreDataLoading) return null;

    // Build raw data maps
    const rawPaths = new Map<string, CablePath[]>();
    const rawJunctions = new Map<string, CableJunction[]>();
    const floorPlanMap = new Map<string, FloorPlanWithScale>();

    filteredFloorPlanIds.forEach((fpId, index) => {
      const paths = pathQueries[index]?.data ?? [];
      const junctions = junctionQueries[index]?.data ?? [];
      const floorPlan = floorPlans.find(fp => fp.id === fpId);

      rawPaths.set(fpId, paths);
      rawJunctions.set(fpId, junctions);
      if (floorPlan) {
        floorPlanMap.set(fpId, floorPlan);
      }
    });

    // Build floor plan summaries
    const floorPlanSummaries: FloorPlanSummary[] = filteredFloorPlanIds.map(fpId => {
      const fp = floorPlanMap.get(fpId);
      return {
        id: fpId,
        name: fp?.name ?? 'Unknown',
        scaleData: fp?.scale_data ?? null,
        unit: getUnit(fp?.scale_data ?? null),
      };
    });

    // Aggregate cable requirements per floor plan
    const cableRequirements: CableRequirementRow[] = [];
    const pathDetails: CablePathDetailRow[] = [];
    const junctionRows: JunctionBoxRow[] = [];
    const conduitMap = new Map<string, ConduitScheduleRow>();

    let totalCableLength = 0;
    let totalPaths = 0;
    let totalJunctions = 0;
    let conduitRunCount = 0;
    const lengthByUnit: Record<string, number> = {};

    filteredFloorPlanIds.forEach(fpId => {
      const paths = rawPaths.get(fpId) ?? [];
      const junctions = rawJunctions.get(fpId) ?? [];
      const floorPlan = floorPlanMap.get(fpId);
      const scaleData = floorPlan?.scale_data ?? null;
      const unit = getUnit(scaleData);
      const floorPlanName = floorPlan?.name ?? 'Unknown';

      // Build junction map for this floor plan
      const junctionMap = buildJunctionMap(junctions);

      // Aggregate cable requirements
      const fpRequirements = aggregateCableRequirements(paths, junctionMap, scaleData);
      for (const req of fpRequirements) {
        cableRequirements.push({
          ...req,
          category: getCableCategoryFromType(req.generic_type),
          floorPlanName,
          floorPlanId: fpId,
        });

        // Update length by unit
        lengthByUnit[unit] = (lengthByUnit[unit] ?? 0) + req.total_length;
        totalCableLength += req.total_length;
      }

      // Build path details
      for (const path of paths) {
        const length = getPathLength(path, junctionMap, scaleData);
        const startJunction = path.start_junction_id ? junctions.find(j => j.id === path.start_junction_id) : null;
        const endJunction = path.end_junction_id ? junctions.find(j => j.id === path.end_junction_id) : null;

        // Calculate cable details with quantities and lengths
        const cableDetails: CableDetail[] = path.cables.map(c => {
          const label = c.cable_ref_type === 'product'
            ? `Product (${c.product_id?.slice(0, 8)}...)`
            : (CABLE_LABELS[c.generic_type as keyof typeof CABLE_LABELS] ?? c.generic_type ?? 'Unknown');
          const cableLength = length * c.qty;
          return {
            qty: c.qty,
            type: label,
            totalLength: Math.round(cableLength * 10) / 10,
          };
        });

        // Calculate total cable quantity and length
        const totalCableQty = path.cables.reduce((sum, c) => sum + c.qty, 0);
        const totalCableLength = Math.round(length * totalCableQty * 10) / 10;

        // Build cables display string showing qty × type = length
        const cablesDisplay = cableDetails.length > 0
          ? cableDetails
              .map(c => `${c.qty}x ${c.type} = ${c.totalLength} ${unit}`)
              .join(', ')
          : '-';

        pathDetails.push({
          id: path.id,
          name: path.name ?? `Path ${paths.indexOf(path) + 1}`,
          floorPlanId: fpId,
          floorPlanName,
          startJunctionName: startJunction?.name ?? null,
          endJunctionName: endJunction?.name ?? null,
          route: PATH_ROUTE_LABELS[path.route] ?? path.route,
          pathType: PATH_TYPE_LABELS[path.path_type] ?? path.path_type,
          conduitSpec: path.conduit ? `${path.conduit.size}" ${path.conduit.material}` : null,
          length: Math.round(length * 10) / 10,
          unit,
          totalCableQty,
          totalCableLength,
          cableDetails,
          cablesDisplay,
        });

        // Count conduit runs and aggregate conduit schedule
        if (path.path_type === 'conduit') {
          conduitRunCount++;

          // Aggregate conduit schedule - include ALL conduit-type paths
          const conduitSize = path.conduit?.size ?? 'Not specified';
          const conduitMaterial = path.conduit?.material ?? 'Not specified';
          const conduitKey = `${conduitSize}|${conduitMaterial}|${unit}`;
          const existing = conduitMap.get(conduitKey);
          if (existing) {
            existing.totalLength += length;
            existing.pathCount += 1;
          } else {
            conduitMap.set(conduitKey, {
              size: conduitSize,
              material: conduitMaterial,
              totalLength: length,
              unit,
              pathCount: 1,
            });
          }
        }

        totalPaths++;
      }

      // Build junction rows
      for (const junction of junctions) {
        const connectedPaths = paths.filter(
          p => p.start_junction_id === junction.id || p.end_junction_id === junction.id
        );

        // Look up actual product name from product_library
        const productName = junction.product_id
          ? productNameMap.get(junction.product_id) ?? 'Unknown Product'
          : null;

        junctionRows.push({
          id: junction.id,
          name: junction.name,
          floorPlanId: fpId,
          floorPlanName,
          productName,
          connectedPathCount: connectedPaths.length,
          notes: junction.notes,
        });

        totalJunctions++;
      }
    });

    // Build conduit schedule from map
    const conduitSchedule: ConduitScheduleRow[] = Array.from(conduitMap.values())
      .map(row => ({
        ...row,
        totalLength: Math.round(row.totalLength * 10) / 10,
      }))
      .sort((a, b) => {
        // Sort by size, then material ("Not specified" goes last)
        const sizeOrder = ['1/2', '3/4', '1', '1-1/4', '1-1/2', '2', '2-1/2', '3', '4'];
        const aIndex = a.size === 'Not specified' ? 999 : sizeOrder.indexOf(a.size);
        const bIndex = b.size === 'Not specified' ? 999 : sizeOrder.indexOf(b.size);
        if (aIndex !== bIndex) return aIndex - bIndex;
        // "Not specified" material goes last within same size
        if (a.material === 'Not specified' && b.material !== 'Not specified') return 1;
        if (a.material !== 'Not specified' && b.material === 'Not specified') return -1;
        return a.material.localeCompare(b.material);
      });

    // Determine primary unit (most common)
    const unitCounts: Record<string, number> = {};
    for (const fp of floorPlanSummaries) {
      unitCounts[fp.unit] = (unitCounts[fp.unit] ?? 0) + 1;
    }
    const primaryUnit = Object.entries(unitCounts)
      .sort((a, b) => b[1] - a[1])[0]?.[0] ?? 'ft';

    // Build summary
    const summary: WiringReportSummary = {
      totalCableLength: Math.round(totalCableLength * 10) / 10,
      primaryUnit,
      totalPaths,
      totalJunctions,
      conduitRunCount,
      lengthByUnit,
    };

    return {
      summary,
      floorPlans: floorPlanSummaries,
      cableRequirements,
      pathDetails,
      junctions: junctionRows,
      conduitSchedule,
      rawPaths,
      rawJunctions,
    };
  }, [floorPlans, filteredFloorPlanIds, pathQueries, junctionQueries, productNameMap, coreDataLoading]);

  return {
    data: reportData,
    isLoading,
    isLoadingProducts: productsLoading,
    error: error as Error | null,
    floorPlans: floorPlans ?? [],
  };
}
